// ============================================================================
// MedicAppointmentsPage — КАБИНЕТ ЗАПИСИ НА ПРИЁМ (пациент и врач).
//
// Один экран, режим зависит от роли (SSoT — JWT/сервер):
//   • Пациент — свои записи, свободные окна и запись, перенос/отмена (если
//     право «Изменение» выдано), напоминания;
//   • Врач/регистратор/психолог — расписание выбранного специалиста, свободные
//     окна, запись пациента, подтверждение/завершение/перенос/отмена.
//
// Данные — только существующие RPC и документы medic_encounters / psy_bookings
// (lib/medicAppointments). Новых контрактов/миграций/полей нет. Все состояния
// (загрузка, нет прав, нет модуля, нет карточки, пусто, ошибка) — явные.
// ============================================================================

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle, CalendarDays, CalendarPlus, Loader2, RefreshCw, Settings2, UserRound,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { OfflineDataBanner } from '@/components/ui/OfflineDataBanner';
import { useAuthStore } from '@/store/authStore';
import { useToastStore } from '@/store/toastStore';
import { PATIENT_ROLE } from '@/lib/patientCabinet';
import {
  PSY_SPECIALIST_ROLES,
  CORE_DEFAULT_DURATION,
  MEDIC_DOCTOR_ROLE,
  MEDIC_NURSE_ROLE,
  MEDIC_REGISTRAR_ROLE,
  buildDaySlots,
  canDo,
  canPatientChange,
  completeAppointment,
  createAppointment,
  isActiveStatus,
  isUpcoming,
  loadAppointmentCapabilities,
  loadDoctorAppointments,
  loadPatientAppointments,
  loadScheduleSettings,
  loadSpecialists,
  loadStatusLabels,
  loadTypeLabels,
  appointmentDoctorCacheKey,
  appointmentIdentityCacheKey,
  appointmentPatientCacheKey,
  localDateKey,
  localSlotLabel,
  rescheduleAppointment,
  resolveAppointmentIdentity,
  saveScheduleSettings,
  serverReminderLabel,
  setAppointmentStatus,
  valueDate,
  valueDateKey,
  type AppointmentCapabilities,
  type AppointmentIdentity,
  type AppointmentItem,
  type AppointmentLoad,
  type AppointmentMode,
  type AppointmentPerson,
  type AppointmentScheduleSettings,
  type AppointmentSource,
} from '@/lib/medicAppointments';
import { useSecurityAccess } from '@/lib/securityRoles';
import { settingsStore } from '@/lib/settingsStore';
import { confirmDialog, promptDialog } from '@/lib/dialogs';
import { friendlyError, logErrorToJournal } from '@/lib/errors';
import { AppointmentCard } from '@/components/medic/appointments/AppointmentCard';
import { AppointmentSlotPicker } from '@/components/medic/appointments/AppointmentSlotPicker';
import { AppointmentBookingDialog } from '@/components/medic/appointments/AppointmentBookingDialog';
import { AppointmentRescheduleDialog } from '@/components/medic/appointments/AppointmentRescheduleDialog';
import { AppointmentScheduleSettingsDialog } from '@/components/medic/appointments/AppointmentScheduleSettingsDialog';
import { AppointmentReminders } from '@/components/medic/appointments/AppointmentReminders';

export interface MedicAppointmentsPageProps {
  embedded?: boolean;
  forceMode?: AppointmentMode;
}

function todayKey(): string {
  return localDateKey(new Date());
}

function useDoctorRole(): boolean {
  const access = useSecurityAccess();
  return access.isAdmin
    || access.roles.includes(MEDIC_DOCTOR_ROLE)
    || access.roles.includes(MEDIC_REGISTRAR_ROLE)
    || access.roles.includes(MEDIC_NURSE_ROLE)
    || (PSY_SPECIALIST_ROLES as readonly string[]).some((role) => access.roles.includes(role));
}

function usePatientRole(): boolean {
  const access = useSecurityAccess();
  return access.isAdmin || access.roles.includes(PATIENT_ROLE);
}

function StateBox({ title, text, onRetry, tone = 'muted' }: {
  title: string;
  text: string;
  onRetry?: () => void;
  tone?: 'muted' | 'warning' | 'danger';
}) {
  const toneClass = tone === 'danger'
    ? 'border-destructive/40 bg-destructive/5'
    : tone === 'warning'
      ? 'border-amber-500/40 bg-amber-500/5'
      : 'border-border bg-card';
  return (
    <div className={`rounded-xl border px-4 py-5 ${toneClass}`}>
      <div className="flex items-start gap-2">
        <AlertTriangle className={`mt-0.5 h-4 w-4 shrink-0 ${tone === 'danger' ? 'text-destructive' : tone === 'warning' ? 'text-amber-600' : 'text-muted-foreground'}`} />
        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium">{title}</div>
          <div className="mt-1 text-xs text-muted-foreground">{text}</div>
          {onRetry && (
            <Button size="sm" variant="outline" className="mt-2 h-7 px-2 text-[11px]" onClick={onRetry}>
              <RefreshCw className="mr-1 h-3 w-3" />
              Повторить
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

function modeTitle(mode: AppointmentMode): string {
  return mode === 'doctor' ? 'Расписание специалиста' : 'Мои записи';
}

function sourceName(source: AppointmentSource): string {
  return source === 'core' ? 'Медицина' : 'Психология';
}

function dateLabel(key: string): string {
  const [year, month, day] = String(key || '').split('-');
  if (!year || !month || !day) return key || '—';
  return `${day}.${month}.${year}`;
}

function countsOf(items: AppointmentItem[], dateKey: string): { upcoming: number; today: number } {
  const now = new Date();
  let upcoming = 0;
  let today = 0;
  for (const item of items) {
    if (!isActiveStatus(item.source, item.statusCode)) continue;
    if (isUpcoming(item.startAt, now)) upcoming += 1;
    if (valueDateKey(item.startAt) === dateKey) today += 1;
  }
  return { upcoming, today };
}

export function MedicAppointmentsPage({ embedded = false, forceMode }: MedicAppointmentsPageProps) {
  const user = useAuthStore((state) => state.user);
  const canDoctorRole = useDoctorRole();
  const canPatientRole = usePatientRole();
  const showSuccess = useToastStore((state) => state.showSuccess);
  const showError = useToastStore((state) => state.showError);
  const showWarning = useToastStore((state) => state.showWarning);
  const showInfo = useToastStore((state) => state.showInfo);

  const allowedModes = useMemo<AppointmentMode[]>(() => {
    const modes: AppointmentMode[] = [];
    if (canDoctorRole) modes.push('doctor');
    if (canPatientRole) modes.push('patient');
    return modes;
  }, [canDoctorRole, canPatientRole]);

  const [modeState, setModeState] = useState<AppointmentMode>(() => (canDoctorRole ? 'doctor' : 'patient'));
  const mode: AppointmentMode = forceMode || modeState;
  const canSwitchMode = !forceMode && allowedModes.length > 1;

  useEffect(() => {
    if (forceMode) {
      setModeState(forceMode);
      return;
    }
    if (allowedModes.length === 1) {
      setModeState(allowedModes[0]);
    }
  }, [forceMode, allowedModes]);

  const [capabilities, setCapabilities] = useState<AppointmentCapabilities | null>(null);
  const [capabilitiesLoading, setCapabilitiesLoading] = useState(true);
  const [capabilitiesError, setCapabilitiesError] = useState('');
  const [specialists, setSpecialists] = useState<AppointmentPerson[]>([]);
  const [specialistsError, setSpecialistsError] = useState('');
  const [statusLabels, setStatusLabels] = useState<Record<AppointmentSource, Record<string, string>>>({ core: {}, psy: {} });
  const [typeLabels, setTypeLabels] = useState<Record<string, string>>({});
  const [identity, setIdentity] = useState<AppointmentIdentity | null>(null);
  const [identityLoading, setIdentityLoading] = useState(false);
  const [identityError, setIdentityError] = useState('');
  const [appointments, setAppointments] = useState<AppointmentLoad | null>(null);
  const [appointmentsLoading, setAppointmentsLoading] = useState(false);
  const [appointmentsError, setAppointmentsError] = useState('');
  const [settings, setSettings] = useState<AppointmentScheduleSettings>(() => loadScheduleSettings());
  const [source, setSource] = useState<AppointmentSource>('core');
  const [doctorId, setDoctorId] = useState('');
  const [dateKey, setDateKey] = useState(todayKey);
  const [durationMin, setDurationMin] = useState(settings.durations.core);
  const [pageSlot, setPageSlot] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);
  const [showPast, setShowPast] = useState(false);
  const [busyKey, setBusyKey] = useState('');
  const [actionError, setActionError] = useState('');
  const [bookingOpen, setBookingOpen] = useState(false);
  const [bookingStartAt, setBookingStartAt] = useState('');
  const [bookingDuration, setBookingDuration] = useState(0);
  const [rescheduleItem, setRescheduleItem] = useState<AppointmentItem | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);

  const refresh = useCallback(() => setRefreshKey((value) => value + 1), []);

  // Настройки сетки — личные настройки пользователя (гидрация асинхронна).
  useEffect(() => {
    let cancelled = false;
    void settingsStore.hydrate().then(() => {
      if (!cancelled) setSettings(loadScheduleSettings());
    }).catch((e: unknown) => {
      logErrorToJournal(e, 'Запись на приём: настройки сетки');
    });
    const unsubscribe = settingsStore.subscribe(() => setSettings(loadScheduleSettings()));
    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, []);

  // Доступность источников/права.
  useEffect(() => {
    let cancelled = false;
    setCapabilitiesLoading(true);
    setCapabilitiesError('');
    loadAppointmentCapabilities()
      .then((loaded) => {
        if (cancelled) return;
        setCapabilities(loaded);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        logErrorToJournal(e, 'Запись на приём: доступность модулей');
        setCapabilitiesError(friendlyError(e));
      })
      .finally(() => {
        if (!cancelled) setCapabilitiesLoading(false);
      });
    return () => { cancelled = true; };
  }, [refreshKey]);

  // Специалисты и подписи статусов.
  useEffect(() => {
    if (!capabilities) return;
    let cancelled = false;
    Promise.all([
      loadSpecialists(capabilities),
      loadStatusLabels('core'),
      loadStatusLabels('psy'),
      loadTypeLabels(),
    ]).then(([specialistResult, coreStatus, psyStatus, types]) => {
      if (cancelled) return;
      setSpecialists(specialistResult.items);
      setSpecialistsError(specialistResult.error);
      setStatusLabels({ core: coreStatus, psy: psyStatus });
      setTypeLabels(types);
    }).catch((e: unknown) => {
      if (cancelled) return;
      logErrorToJournal(e, 'Запись на приём: специалисты/статусы');
      setSpecialistsError(friendlyError(e));
    });
    return () => { cancelled = true; };
  }, [capabilities]);

  // Источник по умолчанию — первый доступный.
  useEffect(() => {
    if (!capabilities) return;
    setSource((current) => {
      if (capabilities[current].available) return current;
      if (capabilities.core.available) return 'core';
      if (capabilities.psy.available) return 'psy';
      return current;
    });
  }, [capabilities]);

  useEffect(() => {
    setDurationMin(source === 'core' ? CORE_DEFAULT_DURATION : settings.durations[source]);
  }, [source, settings]);

  // Специалист и источник должны соответствовать друг другу: при переключении
  // источника выбираем первого специалиста этого направления.
  useEffect(() => {
    if (mode !== 'doctor') return;
    const person = doctorId ? specialists.find((item) => item.id === doctorId) : null;
    if (person && !person.sources.includes(source)) {
      setSource(person.sources[0]);
      return;
    }
    // Специалист не выбран (администратор/регистратор): если в текущем
    // направлении специалистов нет, переключаемся на то, где они есть.
    if (!doctorId && specialists.length > 0
      && !specialists.some((item) => item.sources.includes(source))) {
      setSource(specialists[0].sources[0]);
    }
  }, [mode, doctorId, specialists, source]);

  // Специалист по умолчанию: «свой» (если роль специалиста), иначе один
  // доступный, иначе все (администратор/регистратор).
  useEffect(() => {
    if (mode !== 'doctor') return;
    if (specialists.length === 0) {
      setDoctorId('');
      return;
    }
    setDoctorId((current) => {
      if (current && specialists.some((person) => person.id === current)) return current;
      const mine = specialists.find((person) => person.id === String(user?.id || ''));
      if (mine) return mine.id;
      if (specialists.length === 1) return specialists[0].id;
      return '';
    });
  }, [mode, specialists, user?.id]);

  // Карточка пациента по учётной записи (контакты/привязка).
  useEffect(() => {
    if (mode !== 'patient' || !capabilities) {
      setIdentity(null);
      setIdentityLoading(false);
      setIdentityError('');
      return;
    }
    if (!user?.id) {
      setIdentity(null);
      setIdentityError('Не определён текущий пользователь — войдите заново');
      return;
    }
    let cancelled = false;
    setIdentityLoading(true);
    setIdentityError('');
    resolveAppointmentIdentity({
      id: String(user.id),
      phone: String(user.phone || ''),
      email: String(user.email || ''),
    }).then((loaded) => {
      if (!cancelled) setIdentity(loaded);
    }).catch((e: unknown) => {
      if (cancelled) return;
      logErrorToJournal(e, 'Запись на приём: карточка пациента');
      setIdentityError(friendlyError(e));
    }).finally(() => {
      if (!cancelled) setIdentityLoading(false);
    });
    return () => { cancelled = true; };
  }, [mode, capabilities, user?.id, user?.phone, user?.email, refreshKey]);

  const readyPatientSources = useMemo<AppointmentSource[]>(() => {
    if (!capabilities || !identity) return [];
    return (['core', 'psy'] as AppointmentSource[]).filter((src) =>
      !!identity[src].patient && capabilities[src].available,
    );
  }, [capabilities, identity]);

  // Источник пациента — только тот, где найдена его карточка.
  useEffect(() => {
    if (mode !== 'patient' || readyPatientSources.length === 0) return;
    if (!readyPatientSources.includes(source)) setSource(readyPatientSources[0]);
  }, [mode, readyPatientSources, source]);

  // Записи: пациент — свои; врач — выбранного специалиста (или все).
  useEffect(() => {
    if (!capabilities) return;
    let cancelled = false;

    if (mode === 'patient') {
      if (identityLoading) return;
      if (!identity || readyPatientSources.length === 0) {
        setAppointments(null);
        setAppointmentsLoading(false);
        return;
      }
      setAppointmentsLoading(true);
      setAppointmentsError('');
      loadPatientAppointments(identity, capabilities)
        .then((loaded) => {
          if (!cancelled) setAppointments(loaded);
        })
        .catch((e: unknown) => {
          if (cancelled) return;
          logErrorToJournal(e, 'Запись на приём: записи пациента');
          setAppointmentsError(friendlyError(e));
        })
        .finally(() => {
          if (!cancelled) setAppointmentsLoading(false);
        });
      return () => { cancelled = true; };
    }

    setAppointmentsLoading(true);
    setAppointmentsError('');
    loadDoctorAppointments(doctorId, capabilities)
      .then((loaded) => {
        if (!cancelled) setAppointments(loaded);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        logErrorToJournal(e, 'Запись на приём: расписание специалиста');
        setAppointmentsError(friendlyError(e));
      })
      .finally(() => {
        if (!cancelled) setAppointmentsLoading(false);
      });
    return () => { cancelled = true; };
  }, [capabilities, mode, identity, identityLoading, readyPatientSources, doctorId, refreshKey]);

  useEffect(() => {
    setPageSlot('');
  }, [source, doctorId, dateKey, durationMin]);

  useEffect(() => {
    setAppointments(null);
    setAppointmentsError('');
  }, [mode]);

  useEffect(() => {
    setAppointments(null);
    setAppointmentsError('');
  }, [doctorId]);

  const items = appointments?.items || [];
  const loadWarnings = appointments?.warnings || [];
  const anyAvailable = !!capabilities && (capabilities.core.available || capabilities.psy.available);
  const currentSourceRights = capabilities?.[source].rights ?? null;
  const canCreateCurrentSource = !!capabilities?.[source].available && canDo(currentSourceRights, 'create');
  const canUpdateAnyPatientSource = readyPatientSources.some((src) =>
    capabilities?.[src].available && canDo(capabilities[src].rights, 'update'),
  );
  const patientCreatableSources = useMemo<AppointmentSource[]>(() => {
    if (!capabilities) return [];
    return readyPatientSources.filter((src) =>
      capabilities[src].available && canDo(capabilities[src].rights, 'create'),
    );
  }, [capabilities, readyPatientSources]);
  const canCreate = mode === 'patient' ? patientCreatableSources.length > 0 : canCreateCurrentSource;
  // При смене специалиста старые строки не должны участвовать в сетке/списке
  // до ответа сервера: фильтруем по выбранному специалисту сразу.
  const visibleItems = useMemo(() => {
    if (mode !== 'doctor' || !doctorId) return items;
    return items.filter((item) => item.doctorId === doctorId);
  }, [mode, doctorId, items]);
  const counts = useMemo(() => countsOf(visibleItems, dateKey), [visibleItems, dateKey]);

  const daySlots = useMemo(() => {
    if (mode !== 'doctor' || !doctorId || !capabilities?.[source].available) {
      return { slots: [], closed: false };
    }
    return buildDaySlots(dateKey, source, durationMin, visibleItems, settings);
  }, [mode, doctorId, capabilities, source, dateKey, durationMin, visibleItems, settings]);

  const upcoming = useMemo(() => visibleItems.filter((item) => {
    const start = valueDate(item.startAt);
    return !!start && start.getTime() >= Date.now();
  }), [visibleItems]);
  const past = useMemo(() => visibleItems.filter((item) => {
    const start = valueDate(item.startAt);
    return !!start && start.getTime() < Date.now();
  }), [visibleItems]);
  const dayItems = useMemo(() => visibleItems.filter((item) => valueDateKey(item.startAt) === dateKey), [visibleItems, dateKey]);
  const otherUpcoming = useMemo(() => upcoming.filter((item) => valueDateKey(item.startAt) !== dateKey), [upcoming, dateKey]);

  const bookingSource: AppointmentSource = mode === 'patient'
    ? (patientCreatableSources.includes(source)
      ? source
      : patientCreatableSources[0] || readyPatientSources[0] || source)
    : source;

  // Стабильные props диалога: иначе новый объект capabilities на каждый рендер
  // перезапускает загрузку слотов (эффект useSlotAvailability).
  const dialogCapabilities = useMemo<AppointmentCapabilities>(() => {
    if (!capabilities) {
      const empty = { available: false, state: 'unavailable' as const, reason: '', rights: null };
      return { core: empty, psy: { ...empty } };
    }
    if (!(mode === 'patient' && identity)) return capabilities;
    return {
      core: identity.core.patient ? capabilities.core : { ...capabilities.core, available: false },
      psy: identity.psy.patient ? capabilities.psy : { ...capabilities.psy, available: false },
    };
  }, [mode, identity, capabilities]);

  const dialogFixedPatients = useMemo(() => {
    if (!(mode === 'patient' && identity)) return undefined;
    return {
      core: identity.core.patient || undefined,
      psy: identity.psy.patient || undefined,
    };
  }, [mode, identity]);

  const busy = (item: AppointmentItem): boolean => busyKey === item.key;

  const runAction = async (item: AppointmentItem, action: () => Promise<void>, success: string) => {
    setBusyKey(item.key);
    setActionError('');
    try {
      await action();
      showSuccess(success);
      refresh();
    } catch (e) {
      logErrorToJournal(e, 'Запись на приём: изменение записи');
      setActionError(friendlyError(e));
      showError(friendlyError(e));
    } finally {
      setBusyKey('');
    }
  };

  const handleCreate = async (input: Parameters<typeof createAppointment>[0]): Promise<void> => {
    await createAppointment(input);
    showSuccess('Запись создана');
    setSource(input.source);
    setDateKey(valueDateKey(input.startAt) || dateKey);
    if (mode === 'doctor' && input.doctorId) setDoctorId(input.doctorId);
    refresh();
  };

  const openBooking = (startAt = '', duration = 0) => {
    if (!canCreate) {
      showWarning('Роли не выдано право «Добавление» на записи. Обратитесь к администратору базы.');
      return;
    }
    if (mode === 'patient' && readyPatientSources.length === 0) {
      showWarning('Карточка пациента не найдена — запись недоступна.');
      return;
    }
    if (!specialists.some((person) => person.sources.includes(bookingSource))) {
      showWarning('Нет специалистов, к которым можно записать: назначьте роль «Врач» или «Психолог».');
      return;
    }
    setBookingStartAt(startAt);
    setBookingDuration(duration
      || (mode === 'patient' ? settings.durations[bookingSource] : durationMin));
    setBookingOpen(true);
  };

  const handleCancel = async (item: AppointmentItem) => {
    let reason = '';
    if (item.source === 'psy') {
      const entered = await promptDialog(
        'Причина отмены (необязательно). Она сохранится в комментарии записи.',
        '',
        'Отмена записи',
        'Например: планы изменились',
      );
      if (entered === null) return;
      reason = entered;
    } else if (!(await confirmDialog('Отменить запись на приём?', 'Отмена записи'))) {
      // У medic_encounters нет поля комментария — причина не запрашивается.
      return;
    }
    await runAction(
      item,
      () => setAppointmentStatus(item, 'cancelled', reason ? `Отменено: ${reason}` : 'Отменено'),
      'Запись отменена',
    );
  };

  const handleConfirm = async (item: AppointmentItem) => {
    await runAction(item, () => setAppointmentStatus(item, 'confirmed'), 'Запись подтверждена');
  };

  const handleComplete = async (item: AppointmentItem) => {
    if (!(await confirmDialog('Завершить приём? Запись перейдёт в статус «Выполнен».', 'Завершение приёма'))) return;
    await runAction(item, () => completeAppointment(item), 'Приём завершён');
  };

  const handleReschedule = async (item: AppointmentItem, startAt: string) => {
    await rescheduleAppointment(item, startAt);
    showSuccess('Запись перенесена');
    setDateKey(valueDateKey(startAt) || dateKey);
    refresh();
  };

  const openReschedule = (item: AppointmentItem) => {
    if (!canDo(capabilities?.[item.source].rights ?? null, 'update')) {
      showWarning('Перенос недоступен: роли не выдано право «Изменение» на записи.');
      return;
    }
    setRescheduleItem(item);
  };

  const cardFor = (item: AppointmentItem) => {
    const rights = capabilities?.[item.source].rights ?? null;
    const future = canPatientChange(item);
    const canEdit = canDo(rights, 'update');
    const isDoctorMode = mode === 'doctor';
    const activeStatus = isActiveStatus(item.source, item.statusCode);
    // Врач может закрыть/отменить и прошедшую запись (исправление статуса),
    // пациент — только предстоящую. Перенос — всегда на будущее время.
    return (
      <AppointmentCard
        key={item.key}
        item={item}
        statusLabels={statusLabels[item.source]}
        typeLabels={typeLabels}
        mode={mode}
        canReschedule={future && canEdit}
        canCancel={canEdit && (isDoctorMode ? activeStatus : future)}
        canConfirm={isDoctorMode && item.source === 'psy' && item.statusCode === 'new' && activeStatus && canEdit}
        canComplete={isDoctorMode && activeStatus && canEdit}
        busy={busy(item)}
        serverReminder={isDoctorMode && item.source === 'psy' && activeStatus ? serverReminderLabel(item, 2) : undefined}
        onReschedule={() => openReschedule(item)}
        onCancel={() => void handleCancel(item)}
        onConfirm={() => void handleConfirm(item)}
        onComplete={() => void handleComplete(item)}
      />
    );
  };

  // --- Явные состояния ------------------------------------------------------

  if (!canDoctorRole && !canPatientRole && !forceMode) {
    return (
      <div className="p-3">
        <StateBox
          title="Нет доступа к кабинету записи"
          text="Кабинет доступен пациенту (роль «Пациент»), врачу/регистратору (роли «Врач», «Медицинский регистратор») и психологу. Роли назначает администратор базы."
        />
      </div>
    );
  }

  if (!capabilities) {
    if (capabilitiesError) {
      return (
        <div className="p-3">
          <StateBox title="Не удалось проверить модули записи" text={capabilitiesError} tone="danger" onRetry={refresh} />
        </div>
      );
    }
    return (
      <div className="flex h-full items-center justify-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        Проверяем модули записи…
      </div>
    );
  }

  if (!anyAvailable) {
    const bothDenied = capabilities.core.state === 'denied' && capabilities.psy.state === 'denied';
    const anyError = capabilities.core.state === 'error' || capabilities.psy.state === 'error';
    return (
      <div className="space-y-3 p-3">
        <StateBox
          title={anyError
            ? 'Не удалось проверить модули записи'
            : bothDenied
              ? 'Нет прав на модули записи'
              : 'Модули записи не установлены'}
          text={bothDenied
            ? 'Роли не выданы права на документы записей/картотеку пациентов. Права назначает администратор базы.'
            : 'Нужен документ «Приём» (medic_encounters) и справочник «Пациенты» (medic_patients) из поставки hydra.medic.core либо документ «Запись на приём» (psy_bookings) из поставки психологии.'}
          tone={bothDenied || anyError ? 'warning' : 'muted'}
          onRetry={refresh}
        />
        <div className="grid gap-2 text-[11px] text-muted-foreground sm:grid-cols-2">
          <div className="rounded-lg border px-3 py-2">Медицина: {capabilities.core.reason || 'недоступно'}</div>
          <div className="rounded-lg border px-3 py-2">Психология: {capabilities.psy.reason || 'недоступно'}</div>
        </div>
      </div>
    );
  }

  const patientBlocked = mode === 'patient' && !identityLoading && readyPatientSources.length === 0;
  const patientBlockedText = identity?.core.state === 'ambiguous' || identity?.psy.state === 'ambiguous'
    ? 'Найдено несколько карточек с контактами учётной записи. Автоматически выбрать одну нельзя — обратитесь к администратору/специалисту, чтобы связать карточку.'
    : identity?.core.state === 'denied' || identity?.psy.state === 'denied'
      ? 'Нет прав на чтение картотеки пациентов. Право «Чтение» на справочник пациентов выдаёт администратор базы.'
      : identityError || 'Карточка пациента не найдена по телефону/почте учётной записи. Обратитесь к специалисту: он свяжет учётную запись с карточкой.';

  return (
    <div className={`flex h-full min-h-0 flex-col ${embedded ? '' : 'bg-background'}`}>
      {embedded && (
        <div className="flex shrink-0 items-center gap-2 border-b px-3 py-1.5">
          <CalendarPlus className="h-3.5 w-3.5 text-primary" />
          <span className="text-xs font-medium">Запись на приём</span>
          {canSwitchMode && (
            <div className="flex overflow-hidden rounded-lg border">
              {allowedModes.map((value) => (
                <button
                  key={value}
                  type="button"
                  className={`px-2 py-0.5 text-[10px] ${mode === value ? 'bg-primary text-primary-foreground' : 'hover:bg-accent'}`}
                  onClick={() => setModeState(value)}
                >
                  {modeTitle(value)}
                </button>
              ))}
            </div>
          )}
          <Button size="sm" variant="ghost" className="ml-auto h-7 w-7 p-0" onClick={refresh} title="Обновить">
            <RefreshCw className={`h-3.5 w-3.5 ${appointmentsLoading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      )}
      {!embedded && (
        <div className="flex shrink-0 flex-wrap items-center gap-2 border-b px-3 py-2">
          <CalendarPlus className="h-4 w-4 shrink-0 text-primary" />
          <span className="text-sm font-medium">Запись на приём</span>
          <span className="text-[11px] text-muted-foreground">
            {mode === 'doctor'
              ? `предстоящих: ${counts.upcoming} · на ${dateLabel(dateKey)}: ${counts.today}`
              : `предстоящих: ${counts.upcoming}`}
          </span>
          {canSwitchMode && (
            <div className="ml-2 flex overflow-hidden rounded-lg border">
              {allowedModes.map((value) => (
                <button
                  key={value}
                  type="button"
                  className={`px-2.5 py-1 text-[11px] ${mode === value ? 'bg-primary text-primary-foreground' : 'hover:bg-accent'}`}
                  onClick={() => setModeState(value)}
                >
                  {modeTitle(value)}
                </button>
              ))}
            </div>
          )}
          <div className="ml-auto flex items-center gap-1">
            {mode === 'doctor' && (
              <Button size="sm" variant="outline" className="h-7 px-2 text-[11px]" onClick={() => setSettingsOpen(true)}>
                <Settings2 className="mr-1 h-3 w-3" />
                Сетка
              </Button>
            )}
            <Button size="sm" variant="outline" className="h-7 w-7 p-0" onClick={refresh} disabled={capabilitiesLoading} title="Обновить">
              <RefreshCw className={`h-3.5 w-3.5 ${appointmentsLoading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>
      )}

      <div className="min-h-0 flex-1 space-y-3 overflow-auto p-3">
        {!anyAvailable ? null : (
          <>
            {(!capabilities.core.available || !capabilities.psy.available) && (
              <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 px-3 py-2 text-[11px] text-amber-800 dark:text-amber-300">
                {!capabilities.core.available && <div>Медицина: {capabilities.core.reason || 'недоступно'}</div>}
                {!capabilities.psy.available && <div>Психология: {capabilities.psy.reason || 'недоступно'}</div>}
              </div>
            )}

            {loadWarnings.length > 0 && (
              <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 px-3 py-2 text-[11px] text-amber-800 dark:text-amber-300">
                {loadWarnings.join(' · ')}
              </div>
            )}
            {capabilitiesError && (
              <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 px-3 py-2 text-[11px] text-amber-800 dark:text-amber-300">
                Не удалось обновить права/доступность модулей: {capabilitiesError}
              </div>
            )}
            {specialistsError && (
              <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 px-3 py-2 text-[11px] text-amber-800 dark:text-amber-300">
                {specialistsError}
              </div>
            )}
            {mode === 'patient' && user?.id && (
              <OfflineDataBanner sourceKey={appointmentIdentityCacheKey(String(user.id))} />
            )}
            {mode === 'patient' && readyPatientSources.map((src) => {
              const patientId = identity?.[src].patient?.id || '';
              return patientId
                ? <OfflineDataBanner key={`offline-patient-${src}`} sourceKey={appointmentPatientCacheKey(src, patientId)} />
                : null;
            })}
            {mode === 'doctor' && (['core', 'psy'] as AppointmentSource[])
              .filter((src) => capabilities[src].available)
              .map((src) => (
                <OfflineDataBanner key={`offline-doctor-${src}`} sourceKey={appointmentDoctorCacheKey(src, doctorId)} />
              ))}

            {mode === 'patient' && identityLoading && (
              <div className="flex items-center gap-2 rounded-lg border bg-card px-3 py-3 text-xs text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                Определяем карточку пациента…
              </div>
            )}

            {mode === 'patient' && !identityLoading && readyPatientSources.length > 0 && (
              <div className="flex flex-wrap items-center gap-2 rounded-lg border bg-card px-3 py-2 text-[11px]">
                <UserRound className="h-3.5 w-3.5 text-primary" />
                {readyPatientSources.map((src) => (
                  <span key={src} className="rounded-full border bg-muted/30 px-2 py-0.5">
                    {sourceName(src)}: {identity?.[src].patient?.name}
                  </span>
                ))}
                {identity && (['core', 'psy'] as AppointmentSource[]).filter((src) => !readyPatientSources.includes(src)).map((src) => (
                  <span key={src} className="rounded-full border px-2 py-0.5 text-muted-foreground">
                    {sourceName(src)}: {identity[src].state === 'unlinked' ? 'карточка не связана' : identity[src].error || 'недоступно'}
                  </span>
                ))}
              </div>
            )}

            {mode === 'patient' && patientBlocked && (
              <StateBox
                title={identity?.core.state === 'ambiguous' || identity?.psy.state === 'ambiguous'
                  ? 'Найдено несколько карточек пациента'
                  : identity?.core.state === 'denied' || identity?.psy.state === 'denied'
                    ? 'Нет прав на карточку пациента'
                    : 'Карточка пациента не найдена'}
                text={patientBlockedText}
                tone={identity?.core.state === 'denied' || identity?.psy.state === 'denied' ? 'warning' : 'muted'}
                onRetry={refresh}
              />
            )}

            {mode === 'doctor' && (
              <>
                <AppointmentSlotPicker
                  capabilities={capabilities}
                  specialists={specialists}
                  source={source}
                  onSourceChange={(value) => {
                    setSource(value);
                    const first = specialists.find((person) => person.sources.includes(value));
                    setDoctorId(first?.id || '');
                    setPageSlot('');
                  }}
                  specialistId={doctorId}
                  onSpecialistChange={(id) => { setDoctorId(id); setPageSlot(''); }}
                  dateKey={dateKey}
                  onDateChange={(key) => { setDateKey(key); setPageSlot(''); }}
                  durationMin={durationMin}
                  onDurationChange={(minutes) => { setDurationMin(minutes); setPageSlot(''); }}
                  slots={daySlots.slots}
                  closed={daySlots.closed}
                  loading={!!doctorId && (appointmentsLoading || !appointments)}
                  error={appointments?.state === 'error' || appointments?.state === 'denied' ? (appointments.error || appointmentsError) : ''}
                  selectedStart={pageSlot}
                  onSelectSlot={(slot) => setPageSlot(slot.startAt)}
                  onOpenSettings={() => setSettingsOpen(true)}
                  onRefresh={refresh}
                  patientMode={false}
                />

                {pageSlot && (
                  <div className="flex flex-wrap items-center gap-2 rounded-lg border border-primary/30 bg-primary/5 px-3 py-2 text-xs">
                    <CalendarDays className="h-3.5 w-3.5 text-primary" />
                    Выбрано окно: <span className="font-medium">{localSlotLabel(pageSlot)}</span>
                    {canCreate ? (
                      <Button size="sm" className="ml-auto h-7 px-2 text-[11px]" onClick={() => openBooking(pageSlot, durationMin)}>
                        Записать пациента
                      </Button>
                    ) : (
                      <span className="ml-auto text-[10px] text-muted-foreground">
                        Нет права «Добавление» на записи — запись недоступна.
                      </span>
                    )}
                  </div>
                )}

                {!canCreate && (
                  <div className="rounded-lg border px-3 py-2 text-[11px] text-muted-foreground">
                    Создание записей недоступно роли: нет права «Добавление» на {source === 'core' ? 'medic_encounters' : 'psy_bookings'}.
                  </div>
                )}
              </>
            )}

            {mode === 'doctor' && (
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-[11px] text-muted-foreground">
                  Записи на день ({dayItems.length})
                </span>
                <Button size="sm" className="ml-auto h-7 px-2 text-[11px]" onClick={() => openBooking('')} disabled={!canCreate}>
                  <CalendarPlus className="mr-1 h-3 w-3" />
                  Новая запись
                </Button>
              </div>
            )}

            {mode === 'patient' && (
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-[11px] text-muted-foreground">
                  {canUpdateAnyPatientSource ? 'Свои записи: перенос и отмена доступны до начала приёма.' : 'Свои записи: изменение/отмена недоступны по правам роли.'}
                </span>
                {!canCreate && (
                  <span className="text-[10px] text-amber-700 dark:text-amber-400">
                    Запись недоступна: нет права «Добавление» на документ записей.
                  </span>
                )}
                <Button size="sm" className="ml-auto h-7 px-2 text-[11px]" onClick={() => openBooking('')} disabled={!canCreate || readyPatientSources.length === 0}>
                  <CalendarPlus className="mr-1 h-3 w-3" />
                  Записаться
                </Button>
              </div>
            )}

            {actionError && (
              <div className="rounded-lg border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">
                {actionError}
              </div>
            )}
            {appointmentsError && (
              <StateBox title="Не удалось загрузить записи" text={appointmentsError} tone="danger" onRetry={refresh} />
            )}
            {!appointmentsError && appointments?.state === 'denied' && (
              <StateBox title="Нет прав на чтение записей" text={appointments.error || 'Права роли — SSoT сервера.'} tone="warning" onRetry={refresh} />
            )}
            {!appointmentsError && appointments?.state === 'error' && (
              <StateBox title="Не удалось загрузить записи" text={appointments.error || 'Сервер вернул ошибку.'} tone="danger" onRetry={refresh} />
            )}
            {!appointmentsError && appointments?.state === 'unavailable' && (
              <StateBox title="Записи недоступны" text={appointments.error || 'Документы записей не найдены в базе.'} onRetry={refresh} />
            )}

            {appointmentsLoading && !appointments && (
              <div className="flex items-center gap-2 rounded-lg border bg-card px-3 py-3 text-xs text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                Загрузка записей…
              </div>
            )}

            {!patientBlocked && !appointmentsError && appointments && appointments.state === 'ready' && (
              <>
                {mode === 'doctor' && (
                  <section className="space-y-2">
                    <div className="flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                      <CalendarDays className="h-3.5 w-3.5" />
                      Записи на {dateLabel(dateKey)}
                    </div>
                    {dayItems.length === 0 ? (
                      <div className="rounded-lg border px-3 py-3 text-xs text-muted-foreground">
                        На этот день записей нет.
                      </div>
                    ) : (
                      <div className="space-y-2">{dayItems.map(cardFor)}</div>
                    )}
                  </section>
                )}

                {mode === 'doctor' && (
                  <section className="space-y-2">
                    <div className="flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                      <CalendarDays className="h-3.5 w-3.5" />
                      Предстоящие (кроме выбранного дня)
                    </div>
                    {otherUpcoming.length === 0 ? (
                      <div className="rounded-lg border px-3 py-3 text-xs text-muted-foreground">
                        Других предстоящих записей нет.
                      </div>
                    ) : (
                      <div className="space-y-2">{otherUpcoming.map(cardFor)}</div>
                    )}
                  </section>
                )}

                {mode === 'patient' && (
                  <section className="space-y-2">
                    <div className="flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                      <CalendarDays className="h-3.5 w-3.5" />
                      Предстоящие
                    </div>
                    {upcoming.length === 0 ? (
                      <div className="rounded-lg border px-3 py-3 text-xs text-muted-foreground">
                        Предстоящих записей нет. Нажмите «Записаться», чтобы выбрать свободное окно.
                      </div>
                    ) : (
                      <div className="space-y-2">{upcoming.map(cardFor)}</div>
                    )}
                  </section>
                )}

                {past.length > 0 && (
                  <section className="space-y-2">
                    <button
                      type="button"
                      className="flex w-full items-center gap-1.5 text-[11px] font-medium uppercase tracking-wide text-muted-foreground hover:text-foreground"
                      onClick={() => setShowPast((value) => !value)}
                    >
                      {showPast ? '▾' : '▸'} Прошедшие и завершённые ({past.length})
                    </button>
                    {showPast && <div className="space-y-2">{past.map(cardFor)}</div>}
                  </section>
                )}

                <AppointmentReminders items={visibleItems} mode={mode} />
              </>
            )}

            {!embedded && (
              <div className="pb-2 text-[10px] text-muted-foreground">
                Свободные окна строятся по рабочим часам{mode === 'doctor' ? ' (настройка «Сетка»)' : ''} и занятости
                специалиста из серверных записей. Для психологических записей сервер дополнительно проверяет пересечения интервалов.
              </div>
            )}
          </>
        )}
      </div>

      <AppointmentBookingDialog
        open={bookingOpen}
        mode={mode}
        capabilities={dialogCapabilities}
        specialists={specialists}
        settings={settings}
        initialSource={bookingSource}
        initialDoctorId={doctorId}
        initialDateKey={dateKey}
        initialStartAt={bookingStartAt}
        initialDurationMin={bookingDuration}
        fixedPatients={dialogFixedPatients}
        onClose={() => { setBookingOpen(false); setBookingStartAt(''); setBookingDuration(0); }}
        onOpenSettings={() => setSettingsOpen(true)}
        onSubmit={handleCreate}
      />

      <AppointmentRescheduleDialog
        item={rescheduleItem}
        capabilities={capabilities}
        settings={settings}
        onClose={() => setRescheduleItem(null)}
        onOpenSettings={() => setSettingsOpen(true)}
        allowSettings={mode === 'doctor'}
        onSubmit={handleReschedule}
      />

      <AppointmentScheduleSettingsDialog
        open={settingsOpen}
        settings={settings}
        onClose={() => setSettingsOpen(false)}
        onSave={(next) => {
          setSettings(next);
          saveScheduleSettings(next);
          setPageSlot('');
          showInfo('Сетка рабочих окон сохранена');
        }}
      />
    </div>
  );
}
